import { memo, useCallback } from 'react';
import { PillListActions } from 'src/components/core/PillsInput';

import { Badge, BasicTooltip, CopyText, Label, Pills, Text } from '@/components/core';
import { CloseIcon } from '@/components/icons';
import { useTriggerStore } from '@/store';
import { jsonToList, toString } from '@/utils';

import { HowToGuide } from '../HowToGuide';

export type TriggerEmailConfigurationProps = object;

// eslint-disable-next-line no-empty-pattern
const ITriggerEmailConfiguration = ({}: TriggerEmailConfigurationProps) => {
  const updateAction = useTriggerStore((state) => state.updateAction);
  const action = useTriggerStore((state) => state.action);

  const onWhitelistChange = useCallback(
    (whitelist: Pills.Item[]) => {
      updateAction((action) => {
        action.config.whitelist = whitelist.map((it) => it.value);
        return action;
      });
    },
    [updateAction]
  );

  const initialValues = jsonToList(action?.config?.whitelist).map((v) => ({ value: toString(v) }));

  return (
    <div className="gap-md p-md flex h-full flex-col">
      <div className="flex flex-col gap-2">
        <div className="gap-sm flex items-center">
          <Label>Email address</Label>
          <BasicTooltip>
            <Text>Email address users should send documents to in order to start the AI agent.</Text>
          </BasicTooltip>
        </div>
        <CopyText value={toString(action.config?.email)} />
      </div>

      <div className="flex flex-col gap-1">
        <div className="item-center gap-sm flex">
          <Label>Allow list</Label>
          <BasicTooltip>
            <Text>
              Enter allowed email sources. Each entry may be an exact email address (e.g. user@example.com), a domain
              (e.g. example.com), or a regular expression (e.g. *@example\.(no|com)$).
            </Text>
          </BasicTooltip>
        </div>
        <Pills.Input>
          <Pills.List
            className="flex grow"
            onItemsChange={onWhitelistChange}
            initialValues={initialValues}
            placeholder="john.doe@mydomain.com"
          >
            {(pill: Pills.Item, handle: PillListActions) => (
              <Badge variant="plain">
                <Text>{pill.value}</Text>
                <span className="ml-xs cursor-pointer" onClick={() => handle.removeItem(pill)}>
                  <CloseIcon className="size-3" />
                </span>
              </Badge>
            )}
          </Pills.List>
        </Pills.Input>
      </div>

      <div className="grow" />

      <HowToGuide actionId={action?.actionId} />
    </div>
  );
};

export const TriggerEmailConfiguration = memo(ITriggerEmailConfiguration) as typeof ITriggerEmailConfiguration;
